import React, { useState, useMemo, useEffect, useRef, use, forwardRef, useImperativeHandle, useCallback } from "react";
import Renderer from "../renderer/Renderer";
import { CheckIcon, FullScreenExitIcon, FullScreenIcon, VolumeIcon, VolumeMuteIcon } from "../../assets/icons/icons";
import HotspotPopUp from "./HotspotPopUp";
import BtnLoad from "../../components/common/BtnLoad";
import PublishLoad from "./PublishLoad";

if (typeof document !== "undefined" && !document.getElementById("loader-overlay-style")) {
  const style = document.createElement("style");
  style.id = "loader-overlay-style";
  document.head.appendChild(style);
}

const ViewerComp = forwardRef(
  (
    {
      editorMode = false,
      scene,
      setScene,
      setCurrentHotspot,
      currentHotspot,
      isSelectMode,
      onRendererClickEvents,
      fetchSceneDetails,
      handleBackClick,
    },
    ref
  ) => {
    const [muted, setMuted] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [showHotspotModal, toggleHotspotModal] = useState(false);
    const [sceneLoaded, setSceneLoaded] = useState(false);
    const [reloadTrigger, setReloadTrigger] = useState(0);
    const [isReloading, setIsReloading] = useState(false);
    const [loaderBackgroundImage, setLoaderBackgroundImage] = useState(null);
    const screenshotIntervalRef = useRef(null);
    const [isImageTransitioning, setIsImageTransitioning] = useState(false);
    const [shouldShowImage, setShouldShowImage] = useState(false);
    const [hasTransitionedOnce, setHasTransitionedOnce] = useState(false);
    const [isReloadingScene, setIsReloadingScene] = useState(false);

    const currentSceneRef = useRef(scene.uuid);
    const previousSceneStateRef = useRef({
      isMuted: scene.isMuted,
      loop: scene.loop,
      hotspotTimers: scene.hotspots?.map((h) => h.timer?.start) || [],
    });

    // Function to capture a screenshot of the entire ViewerComp div
    const captureViewerCompScreenshot = useCallback(() => {
      try {
        const viewerElement = document.querySelector(".ThreeDViewerC");
        if (!viewerElement) {
          return Promise.resolve(null);
        }

        // Get the canvas and ensure it's rendered with current camera position
        const canvas = viewerElement.querySelector("canvas");
        if (!canvas) {
          return Promise.resolve(null);
        }

        // Always force render to ensure we have the current view
        if (window._threeDViewerRenderer && window._threeDViewerScene && window._threeDViewerCamera) {
          // Get current camera position
          const camera = window._threeDViewerCamera;

          // Force render with current camera
          window._threeDViewerRenderer.render(window._threeDViewerScene, camera);
        } else {
          return Promise.resolve(null);
        }

        // Capture immediately after render
        return new Promise((resolve) => {
          try {
            // Create a temporary canvas for the screenshot
            const tempCanvas = document.createElement("canvas");
            const tempCtx = tempCanvas.getContext("2d");

            // Use the renderer's canvas directly
            const rendererCanvas = window._threeDViewerRenderer.domElement;

            // Set dimensions to match the renderer canvas size
            const targetWidth = rendererCanvas.width;
            const targetHeight = rendererCanvas.height;

            tempCanvas.width = targetWidth;
            tempCanvas.height = targetHeight;

            // Draw the renderer canvas content directly without scaling
            tempCtx.drawImage(rendererCanvas, 0, 0, targetWidth, targetHeight);

            // Convert to data URL with lower quality for better performance
            const dataUrl = tempCanvas.toDataURL("image/jpeg", 0.6);

            // Clean up temporary canvas
            tempCanvas.width = 0;
            tempCanvas.height = 0;

            resolve(dataUrl);
          } catch (error) {
            console.error("Error in screenshot capture:", error);
            resolve(null);
          }
        });
      } catch (error) {
        console.error("Error capturing ViewerComp screenshot:", error);
        return Promise.resolve(null);
      }
    }, [sceneLoaded]);

    // Helper function to get hotspot timer values for comparison
    const getHotspotTimers = (hotspots) => {
      return hotspots?.map((h) => h.timer?.start) || [];
    };

    // Initialize previous state reference when component first loads
    useEffect(() => {
      previousSceneStateRef.current = {
        isMuted: scene.isMuted,
        loop: scene.loop,
        hotspotTimers: getHotspotTimers(scene.hotspots),
      };
    }, []); // Empty dependency array - only run once on mount


    // Memoize sceneDetails to prevent unnecessary reloads in Renderer
    const sceneDetails = useMemo(
      () => ({
        ...scene,
        reloadTrigger, // Include reloadTrigger to force re-render when needed
      }),
      [scene.cameraView, scene.uuid, scene.inventory.type, scene.hotspots, scene.isMuted, scene.loop, reloadTrigger]
    );

    // Sync currentHotspot changes back to scene.hotspots array
    useEffect(() => {
      if (currentHotspot?.uuid && scene?.hotspots) {
        const hotspotIndex = scene.hotspots.findIndex((h) => h.uuid === currentHotspot.uuid);
        if (hotspotIndex !== -1) {
          // Only update if the hotspot data has actually changed
          const existingHotspot = scene.hotspots[hotspotIndex];
          const hasChanged = JSON.stringify(existingHotspot) !== JSON.stringify(currentHotspot);

          if (hasChanged) {           
            setScene((prevScene) => ({
              ...prevScene,
              hotspots: prevScene.hotspots.map((hotspot, index) => (index === hotspotIndex ? currentHotspot : hotspot)),
            }));
          }
        }
      }
    }, [currentHotspot, scene?.hotspots]);

    const resetStates = () => {
      toggleHotspotModal(false);
      // Don't clear currentHotspot during reloads to preserve state
      // setCurrentHotspot({});
      setSceneLoaded(false);
      // Reset transition state for new scene
      setHasTransitionedOnce(false);
      setShouldShowImage(false);
      setIsImageTransitioning(false);
      // Don't clear the loader background image - let continuous capture handle it
      // setLoaderBackgroundImage(null);
    };

    // Handle scene reload and video restart when scene.isMuted, scene.loop, or hotspot timers change
    useEffect(() => {
      // Prevent infinite reloads
      if (isReloading) {
 
        return;
      }

      const currentTimers = getHotspotTimers(scene.hotspots);
      const previousTimers = previousSceneStateRef.current.hotspotTimers;

      const hasScenePropertiesChanged =
        scene.isMuted !== previousSceneStateRef.current.isMuted || scene.loop !== previousSceneStateRef.current.loop;

      const hasHotspotTimersChanged =
        currentTimers.length !== previousTimers.length ||
        currentTimers.some((timer, index) => timer !== previousTimers[index]);

      // Only reload for scene property changes, not hotspot timer changes
      if (hasScenePropertiesChanged) {
        // Only reload if scene is already loaded (to avoid unnecessary reloads during initial load)
        if (sceneLoaded) {
          setIsReloading(true);
          setIsReloadingScene(true); // Mark that we're reloading the scene

          // Reset states
          resetStates();
          setIsLoading(true);

          // Force scene reload by incrementing reloadTrigger
          setReloadTrigger((prev) => {
            const newValue = prev + 1;
            return newValue;
          });

          // Restart video if it's a video scene
          if (scene.inventory.type === "VIDEO" && window._threeDViewerVideo) {
            const video = window._threeDViewerVideo;
            video.currentTime = 0;
            video.play().catch(console.error);
          }
        }

        // Update the previous state reference
        previousSceneStateRef.current = {
          isMuted: scene.isMuted,
          loop: scene.loop,
          hotspotTimers: currentTimers,
        };
      } else if (hasHotspotTimersChanged) {
        // For hotspot timer changes, just update the reference without reloading

        // Update the previous state reference
        previousSceneStateRef.current = {
          isMuted: scene.isMuted,
          loop: scene.loop,
          hotspotTimers: currentTimers,
        };
      }
    }, [scene.isMuted, scene.loop, scene.hotspots, sceneLoaded, isReloading]);

    // Handle editor mode changes
    useEffect(() => {
      if (scene.inventory.type === "IMAGE") {
        setIsTransitioning(true);
        // Small delay to ensure smooth transition
        const timer = setTimeout(() => {
          setIsTransitioning(false);
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [editorMode, scene.inventory.type]);

    const handleMuteToggle = () => {
      // Only allow mute toggle if scene.isMuted is false
      if (scene.isMuted) {
        return;
      }

      // Only update local muted state - let Renderer handle video muted property
      setMuted(!muted);
    };

    const handleSceneChange = (sceneObj) => {
      if (sceneObj.uuid === currentSceneRef.current) {
        setScene((prev) => ({
          ...prev,
          cameraView: sceneObj.cameraView,
        }));

        if (sceneObj.type === "VIDEO" && window._threeDViewerVideo) {
          window._threeDViewerVideo.currentTime = 0;
          window._threeDViewerVideo.play().catch(console.error);
        }
        return;
      }

      setMuted(sceneObj.isMuted);

      // Check if animation is already in progress (from hotspot click)
      const isAnimationInProgress = shouldShowImage || isImageTransitioning;

      if (!isAnimationInProgress) {
        // Only reset animation state if no animation is currently running
        setHasTransitionedOnce(false);
        setShouldShowImage(false);
        setIsImageTransitioning(false);

        // Only capture screenshot and trigger animation if not from hotspot click
        // (hotspot clicks handle their own animation)
        if (!isLoading) {
          captureViewerCompScreenshot().then((screenshot) => {
            if (screenshot) {
              setLoaderBackgroundImage(screenshot);
              // Immediately set shouldShowImage to true to trigger transition
              setShouldShowImage(true);
            }
          });
        }
      } else {
      }

      // Set loading state and change scene
      setIsLoading(true);
      currentSceneRef.current = sceneObj.uuid;
      setScene(sceneObj);

      // Don't reset states immediately - let the transition complete
      // The handleSceneLoaded function will handle resetting states
    };

    // Expose handleSceneChange function to parent component
    useImperativeHandle(ref, () => ({
      handleSceneChange,
    }));

    // useEffect(()=> {
    //   if (scene.uuid) handleSceneChange(scene)
    // }, [scene.uuid])
    const handleFullScreenToggle = () => {
      const viewerElement = document.querySelector(".ThreeDViewerC");
      if (!viewerElement) return;

      if (!document.fullscreenElement) {
        viewerElement.requestFullscreen().catch((err) => {
          console.error(`Error attempting to enable full-screen mode: ${err.message}`);
        });
        setIsFullScreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          setIsFullScreen(false);
        }
      }
    };

    const handleHotspotClick = (uuid) => {
      window.postMessage({
        type: "HOTSPOT_CLICKED",
        uuid: uuid,
      });
    };

    const handleSceneLoaded = () => {
      setIsLoading(false);
      setSceneLoaded(true);
      setIsReloading(false);
      setIsReloadingScene(false); // Reset scene reloading flag
      // Don't clear the loader background image - let continuous capture handle it
      // setLoaderBackgroundImage(null);
      if (isTransitioning) {
        setIsTransitioning(false);
      }
      // Note: Don't reset animation states here - let the animation complete naturally
      // The animation will complete based on its own timers, not scene loading state
      // This ensures the 2-second animation always completes regardless of scene loading speed
    };

    // Handle fullscreen changes
    useEffect(() => {
      const handleFullScreenChange = () => {
        setIsFullScreen(!!document.fullscreenElement);
      };

      document.addEventListener("fullscreenchange", handleFullScreenChange);
      return () => {
        document.removeEventListener("fullscreenchange", handleFullScreenChange);
      };
    }, []);

    // Function to capture current view immediately
    const captureCurrentView = () => {
      // Only force render if scene is not fully loaded (to prevent hotspot flickering)
      if (!sceneLoaded && window._threeDViewerRenderer && window._threeDViewerScene && window._threeDViewerCamera) {
        window._threeDViewerRenderer.render(window._threeDViewerScene, window._threeDViewerCamera);
      }

      // Small delay to ensure render is complete, then capture
      setTimeout(() => {
        captureViewerCompScreenshot().then((screenshot) => {
          if (screenshot) {
            setLoaderBackgroundImage(screenshot);
          }
        });
      }, 100);
    };

    // Manage image visibility and transitions based on loading state
    useEffect(() => {
      const isCurrentlyLoading = isLoading || isTransitioning || !scene;

      // Only prevent animation triggers during scene reloads if animation hasn't started yet
      // Once animation is in progress, let it continue regardless of scene reloads
      if (isReloadingScene && !isImageTransitioning && !shouldShowImage) {
        return;
      }

      // Only show image transition if it hasn't happened once yet
      // Remove the isCurrentlyLoading check to ensure animation always starts when screenshot is available
      if (loaderBackgroundImage && !shouldShowImage && !hasTransitionedOnce) {
        // Show image when screenshot is available (regardless of loading state)
        setShouldShowImage(true);
      } else if (shouldShowImage && !isImageTransitioning && !hasTransitionedOnce) {
        // Start transition animation when image should be shown but transition hasn't started
        

        // Start transition animation immediately
        
        setIsImageTransitioning(true);
      } else if (shouldShowImage && isImageTransitioning && !hasTransitionedOnce) {
        // Once animation starts, let it complete regardless of loading state
        // This ensures the animation always completes even if scene loading finishes early
        // The animation timers are completely independent of scene loading state
         

        // Start fade out when zoom is 40% complete
        const fadeTimer = setTimeout(() => {
           
          // Fade out starts at 40% zoom
        }, 480); // Start fade at 40% zoom (0.48s)

        // Hide image completely when zoom reaches 90%
        const hideTimer = setTimeout(() => {
          
          setShouldShowImage(false);
          setIsImageTransitioning(false);
          setHasTransitionedOnce(true); // Mark that transition has occurred once
        }, 1080); // Hide when zoom reaches 90% (1.08s)

        return () => {
          clearTimeout(fadeTimer);
          clearTimeout(hideTimer);
        };
      }
    }, [loaderBackgroundImage, shouldShowImage, isImageTransitioning, hasTransitionedOnce, isReloadingScene]);

    // Hide image immediately when transition is complete
    useEffect(() => {
      // Only hide image if animation has completed AND we're not in the middle of a transition
      // This prevents the animation from being interrupted by scene loading state changes
      if (hasTransitionedOnce && shouldShowImage && !isImageTransitioning) {
        setShouldShowImage(false);
        setIsImageTransitioning(false);
      }

      // Note: Don't automatically reset hasTransitionedOnce here
      // It will be reset manually when a new hotspot is clicked or scene changes
      // This prevents infinite loops
    }, [shouldShowImage, isImageTransitioning, hasTransitionedOnce]);

    // Continuous screenshot capture every 500ms
    useEffect(() => {
      // Start continuous screenshot capture
      const startScreenshotCapture = () => {
        if (screenshotIntervalRef.current) {
          clearInterval(screenshotIntervalRef.current);
        }

        screenshotIntervalRef.current = setInterval(() => {
          // Don't update if image is currently visible
          if (shouldShowImage) {
            return;
          }

          // Don't capture if scene is loading, transitioning, or not loaded
          if (isLoading || isTransitioning || !sceneLoaded) {
            return;
          }

          // Only capture if renderer is available and scene is loaded
          if (window._threeDViewerRenderer && window._threeDViewerScene && window._threeDViewerCamera) {
            captureViewerCompScreenshot().then((screenshot) => {
              if (screenshot) {
                setLoaderBackgroundImage(screenshot);
              }
            });
          }
        }, 500);
      };

      // Start capturing after a short delay to ensure renderer is ready
      const timer = setTimeout(startScreenshotCapture, 1000);

      return () => {
        clearTimeout(timer);
        if (screenshotIntervalRef.current) {
          clearInterval(screenshotIntervalRef.current);
          screenshotIntervalRef.current = null;
        }
      };
    }, [shouldShowImage, sceneLoaded, isLoading, isTransitioning, captureViewerCompScreenshot]);

    useEffect(() => {
      const listener = (event) => {
        if (event.data) {
          if (event.data.type === "HOTSPOT_CLICKED" && event.data.uuid) {
            var hotspot = scene.hotspots.filter((e) => e.uuid === event.data.uuid)[0];

            setCurrentHotspot(hotspot);
            if (!editorMode) {
              if (hotspot.type === "MEDIA") {
                toggleHotspotModal(true);
              }
              if (!editorMode) {
                if (hotspot.type === "LINK") {
                   

                  // Reset animation state for new scene transition
                  
                  setHasTransitionedOnce(false);
                  setShouldShowImage(false);
                  setIsImageTransitioning(false);

                  // Capture screenshot and start animation immediately
                  captureViewerCompScreenshot()
                    .then((screenshot) => {
                      if (screenshot) {
                       
                        setLoaderBackgroundImage(screenshot);
                        setShouldShowImage(true);
                        setIsLoading(true);

                        // Delay the scene change to allow animation to start
                        setTimeout(() => {
                         
                          fetchSceneDetails(hotspot.destination);
                        }, 100);
                      } else {
                        console.warn(
                          `[${new Date().toISOString()}] 🎬 HOTSPOT CLICK: Failed to capture screenshot, proceeding with scene change`
                        );
                        // If screenshot fails, still proceed with scene change
                        setIsLoading(true);
                        setTimeout(() => {
                         
                          fetchSceneDetails(hotspot.destination);
                        }, 100);
                      }
                    })
                    .catch((error) => {
                      console.error(
                        `[${new Date().toISOString()}] 🎬 HOTSPOT CLICK: Error capturing screenshot:`,
                        error
                      );
                      // If screenshot fails, still proceed with scene change
                      setIsLoading(true);
                      setTimeout(() => {
                         
                        fetchSceneDetails(hotspot.destination);
                      }, 100);
                    });
                }
              }
            }
          }
        }
      };
      window.addEventListener("message", listener);
      return () => window.removeEventListener("message", listener);
    }, [scene, editorMode]);
    return (
      <div className={`ThreeDViewerC ${isSelectMode ? "select-mode-active" : ""}`} style={{ position: "relative" }}>

        {/* Screenshot image - shows during loading with transition and fade effects */}
        {shouldShowImage && loaderBackgroundImage && (
          <img
            src={loaderBackgroundImage}
            alt="Scene screenshot"
            className={`prev-scene-loader ${isImageTransitioning ? "transitioning" : "fading"}`}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 9999,
              border: "5px solid red", // Bright red border
              backgroundColor: "red", // Red background to make it visible
            }}
            // onLoad={() => console.log("Transition image loaded successfully")}
            // onError={(e) => console.error("Transition image failed to load:", e)}
          />
        )}

        {/* Click blocker overlay - prevents interactions during loading */}
        {(isLoading || isTransitioning || !scene) && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 10000,
              pointerEvents: "auto",
              cursor: "wait",
            }}
          />
        )}

        {/* Loader cube - visible only if scene is still loading after transition completes */}
        {(isLoading || isTransitioning || !scene) && !shouldShowImage && hasTransitionedOnce && (
          <div className="loader-overlay">
            <div className="loader-container">
              <div className="loader-cube">
                <div className="loader-side front"></div>
                <div className="loader-side back"></div>
                <div className="loader-side left"></div>
                <div className="loader-side right"></div>
                <div className="loader-side top"></div>
                <div className="loader-side bottom"></div>
              </div>
            </div>
          </div>
        )}

        {/* Select Mode Blur Overlay */}
        {isSelectMode && (
          //   <div className="Scene-delay">
          //   <p>🎬 Start by selecting a media file for this scene</p>
          //   <p>🖼 Images or 🎥 videos — your scene starts with content</p>
          // </div>
          <div className="select-mode-overlay">
            <div className="top-left">Left-click to place a hotspot. Right-click to pan.</div>
            <div className="bottom-right">Add comment, More actions Press Esc to cancel</div>
          </div>
        )}

        <Renderer
          sceneDetails={sceneDetails}
          muted={scene.isMuted ? true : muted || showHotspotModal}
          onLoaded={handleSceneLoaded}
          loading={isLoading || isTransitioning}
          editorMode={editorMode}
          onHotspotClick={handleHotspotClick}
          isSelectMode={isSelectMode}
          onRendererClickEvents={onRendererClickEvents}
        />
        {scene.description && !isSelectMode && (
          <div className="scene-description">
            <p> {scene.description} </p>
          </div>
        )}
        <div className="quickTools">
          {scene.inventory.type === "VIDEO" &&
            (scene.isMuted ? (
              <span className="fullScreen muted-scene">
                <VolumeMuteIcon />
              </span>
            ) : (
              <span onClick={handleMuteToggle} className="fullScreen">
                {muted ? <VolumeMuteIcon /> : <VolumeIcon />}
              </span>
            ))}
          <span onClick={handleFullScreenToggle} className="fullScreen">
            {isFullScreen ? <FullScreenExitIcon /> : <FullScreenIcon />}
          </span>
        </div>
        <HotspotPopUp
          currentHotspot={currentHotspot}
          showHotspotModal={showHotspotModal}
          onClose={() => toggleHotspotModal(false)}
          toggleHotspotModal={toggleHotspotModal}
        />
      </div>
    );
  }
);

export default ViewerComp;
